import React, { useState } from 'react';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'tel' | 'email' | 'textarea' | 'choice' | 'select';
  required?: boolean;
  options?: string[];
  autoComplete?: string;
  placeholder?: string;
  full?: boolean;
}
interface EmbedFormProps {
  embedId: string;
  fields: FormField[];
  submitLabel: string;
  successTitle: string;
  successText: string;
  footnote?: string;
  className?: string;
}
type Status = 'idle' | 'submitting' | 'success';
const inputBase = 'w-full rounded-md border border-navy/15 bg-white px-4 text-base text-ink placeholder:text-ink-soft/70 transition-colors duration-150 focus:border-water focus:outline-none focus:ring-2 focus:ring-water/30';
export function EmbedForm({
  embedId,
  fields,
  submitLabel,
  successTitle,
  successText,
  footnote,
  className
}: EmbedFormProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const update = (name: string, value: string) => {
    setValues(v => ({
      ...v,
      [name]: value
    }));
    if (errors[name]) setErrors(e => ({
      ...e,
      [name]: ''
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    fields.forEach(f => {
      const val = (values[f.name] || '').trim();
      if (f.required && !val) next[f.name] = `${f.label} is required`;
      if (f.type === 'email' && val && !/^\S+@\S+\.\S+$/.test(val)) next[f.name] = 'Enter a valid email';
      if (f.type === 'tel' && val && val.replace(/\D/g, '').length < 10) next[f.name] = 'Enter a 10-digit phone number';
    });
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  };
  return <div className={twMerge('rounded-xl bg-white p-6 shadow-[0_1px_2px_rgba(7,27,71,0.06),0_12px_40px_-12px_rgba(7,27,71,0.18)] sm:p-8', className)} data-id="element-82" data-mp="9iusdg2n1awt">
      <p className="mb-5 flex items-center gap-2 text-xs font-medium text-ink-soft" data-id="element-83" data-mp="ei7a5menjrsj">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" data-id="element-84" data-mp="jg0hiiroyrue" />
        Connected to 1Dash.Pro
      </p>
      {/* 1Dash.Pro embed slot — replace the fallback form inside this div with the live 1Dash.Pro form */}
      <div id={embedId} data-embed-provider="1dash-pro" data-id="element-85" data-mp="sb6uc1pdqa6f">
        {status === 'success' ? <div role="status" className="flex flex-col items-start gap-3 py-8" data-id="element-86" data-mp="xa9vvc65hy9n">
            <CheckCircle2Icon className="h-10 w-10 text-water" aria-hidden="true" data-id="element-87" data-mp="127t9c9v0y70j" />
            <h3 className="display text-3xl text-navy" data-id="element-88" data-mp="1751umjbgyr60">{successTitle}</h3>
            <p className="text-ink-muted" data-id="element-89" data-mp="33z6couhcg6uh">{successText}</p>
            <button type="button" onClick={() => {
          setValues({});
          setStatus('idle');
        }} className="mt-2 text-sm font-semibold text-navy underline underline-offset-4 hover:text-water-600" data-id="element-90" data-mp="340pwuw64f21j">
              Send another
            </button>
          </div> : <form onSubmit={handleSubmit} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-91" data-mp="1hb33gxvm7j5k">
            {fields.map(f => {
          const id = `${embedId}-${f.name}`;
          const err = errors[f.name];
          const span = f.full || f.type === 'textarea' || f.type === 'choice' ? 'sm:col-span-2' : '';
          return <div key={f.name} className={span} data-id="element-92" data-mp="200j5jpmy7jo3">
                  {f.type === 'choice' ? <fieldset data-id="element-93" data-mp="1v1yg8l98ikef">
                      <legend className="mb-2 text-sm font-semibold text-ink" data-id="element-94" data-mp="1v0n33utwg0an">{f.label}</legend>
                      <div className="grid grid-cols-3 gap-2" data-id="element-95" data-mp="omnqegw1nawv">
                        {f.options?.map(opt => {
                  const active = values[f.name] === opt;
                  return <label key={opt} className={twMerge('flex min-h-[48px] cursor-pointer items-center justify-center rounded-md border text-sm font-semibold transition-colors duration-150', active ? 'border-navy bg-navy text-white' : 'border-navy/15 text-ink hover:border-navy/40')} data-id="element-96" data-mp="27ho59ce5u8ym">
                              <input type="radio" name={id} value={opt} checked={active} onChange={() => update(f.name, opt)} className="sr-only" data-id="element-97" data-mp="2v7s6jd3vlwmg" />
                              {opt}
                            </label>;
                })}
                      </div>
                    </fieldset> : <>
                      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-98" data-mp="y9b4e6cs1zng">
                        {f.label}
                        {f.required && <span className="text-water-600" data-id="element-99" data-mp="1c7rjakksmwgo"> *</span>}
                      </label>
                      {f.type === 'textarea' ? <textarea id={id} rows={3} value={values[f.name] || ''} onChange={e => update(f.name, e.target.value)} placeholder={f.placeholder} aria-invalid={!!err} className={twMerge(inputBase, 'py-3', err && 'border-red-500')} data-id="element-100" data-mp="jmhyfrp5e67x" /> : f.type === 'select' ? <select id={id} value={values[f.name] || ''} onChange={e => update(f.name, e.target.value)} className={twMerge(inputBase, 'min-h-[48px]')} data-id="element-101" data-mp="3s08fkvwfsc7l">
                          <option value="" data-id="element-102" data-mp="25ozn0tj8lq2u">Choose a week</option>
                          {f.options?.map(o => <option key={o} value={o} data-id="element-103" data-mp="2alq5fniikxrf">
                              {o}
                            </option>)}
                        </select> : <input id={id} type={f.type} inputMode={f.type === 'tel' ? 'tel' : undefined} autoComplete={f.autoComplete} value={values[f.name] || ''} onChange={e => update(f.name, e.target.value)} placeholder={f.placeholder} aria-invalid={!!err} aria-describedby={err ? `${id}-err` : undefined} className={twMerge(inputBase, 'min-h-[48px]', err && 'border-red-500')} data-id="element-104" data-mp="1hym35qat2xjc" />}
                      {err && <p id={`${id}-err`} className="mt-1 text-sm text-red-600" data-id="element-105" data-mp="hw7cfl0n55la">
                          {err}
                        </p>}
                    </>}
                </div>;
        })}
            <div className="sm:col-span-2" data-id="element-106" data-mp="30hxu7xsbuql">
              <button type="submit" disabled={status === 'submitting'} className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-md bg-navy px-6 text-base font-semibold text-white transition-colors duration-150 hover:bg-navy-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water focus-visible:ring-offset-2 disabled:opacity-80" data-id="element-107" data-mp="3v529ecatuoo1">
                {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-108" data-mp="xa1rvhopzjgx" />}
                {status === 'submitting' ? 'Sending…' : submitLabel}
              </button>
              {footnote && <p className="mt-3 text-sm text-ink-soft" data-id="element-109" data-mp="nixegmep06o7">{footnote}</p>}
            </div>
          </form>}
      </div>
    </div>;
}